import { useMutation } from "@apollo/client/react";
import { useCallback, useEffect, useRef } from "react";
import { gql } from "graphql-tag";
import { captureException } from "@sentry/nextjs";
import { logger } from "@portal/app/_common/lib/logger";
import { useAuthenticatedContext } from "@portal/app/_components/context/AuthenticatedContext";
import { UpdateCustomerDetailsHostedResultDocument } from "@portal/app/_components/formFunnel/results/generated";
import { SaveFormFunnelSessionAnswersDocument } from "./generated";
import { buildSaveVariables } from "@portal/app/_components/formFunnel/utils/buildSaveVariables";
import type { PayloadFormQuestion } from "@portal/app/_server/services/formFunnel/payloadTypes";

void gql`
  mutation saveFormFunnelSessionAnswers($sessionId: ID!, $funnelId: ID!, $answers: Json!) {
    saveFormFunnelSessionAnswers(sessionId: $sessionId, funnelId: $funnelId, answers: $answers) {
      id
    }
  }
`;

interface UseIncrementalSaveInput {
  sessionId?: string;
  funnelId?: string;
}

export const useIncrementalSave = (session?: UseIncrementalSaveInput) => {
  const { customerId } = useAuthenticatedContext();
  const [updateCustomerDetails] = useMutation(UpdateCustomerDetailsHostedResultDocument);
  const [saveFormFunnelSessionAnswers] = useMutation(SaveFormFunnelSessionAnswersDocument);

  const saveSessionAnswers = useCallback(
    async (answers: Record<string, unknown>): Promise<void> => {
      if (!customerId || !session?.sessionId || !session.funnelId) {
        return;
      }

      try {
        await saveFormFunnelSessionAnswers({
          variables: {
            sessionId: session.sessionId,
            funnelId: session.funnelId,
            answers,
          },
        });
      } catch (error) {
        logger.error(error, "[useIncrementalSave] Failed to save form funnel session answers");
        captureException(error, {
          tags: { component: "useIncrementalSave", operation: "saveFormFunnelSessionAnswers" },
        });
      }
    },
    [customerId, saveFormFunnelSessionAnswers, session?.funnelId, session?.sessionId]
  );

  const saveAnswer = useCallback(
    async (fieldKey: string, value: unknown, question?: PayloadFormQuestion): Promise<void> => {
      if (!customerId) {
        return;
      }

      if (value !== undefined && value !== null && value !== "") {
        await saveSessionAnswers({ [fieldKey]: value });
      }

      const variables = buildSaveVariables(fieldKey, value, question);
      if (!variables) {
        return;
      }

      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          await updateCustomerDetails({ variables: { data: variables } });
          return;
        } catch (error) {
          if (attempt === 0) {
            logger.warn(error, `[useIncrementalSave] Retry for ${fieldKey}`);
            await new Promise((r) => setTimeout(r, 500));
            continue;
          }

          logger.error(error, `[useIncrementalSave] Failed to save ${fieldKey} after retry`);
          // Rule of Log and Catch: intentionally violated — incremental save is
          // best-effort and should not block the funnel.
          captureException(error, {
            tags: { component: "useIncrementalSave", fieldKey },
          });
          return;
        }
      }
    },
    [customerId, saveSessionAnswers, updateCustomerDetails]
  );

  const saveAnswerRef = useRef(saveAnswer);
  useEffect(() => {
    saveAnswerRef.current = saveAnswer;
  }, [saveAnswer]);

  const stableSaveAnswer = useCallback(
    (fieldKey: string, value: unknown, question?: PayloadFormQuestion) =>
      saveAnswerRef.current(fieldKey, value, question),
    []
  );

  return { saveAnswer: stableSaveAnswer, saveSessionAnswers };
};
